import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';

// Removing a filter chip of a result list that searches with ProfessionModule (Arbeit, Bildung).
// Each function changes the search only; the list then reloads it from page 1.

export const removeProfessionChip = (index: number): void => {
  const professions = (ProfessionModule.searchJobProfessionTitle || []).filter((_: any, i: number) => i !== index);
  ProfessionModule.SetSearchJobProfession(professions);
  ProfessionModule.SetSearchJobProfessionTitle(professions);
  ProfessionModule.SetSearch({ obj: 'profession_id', value: professions.map((v: any) => v.id).join(',') || null });
};

export const removeLocationChip = (): void => {
  ProfessionModule.SetSearchJobLocation(null);
  ProfessionModule.SetSearchJobLocationTitle(null);
  ProfessionModule.SetSearch({ obj: 'location_id', value: null });
};

export const removeStateChip = (id: string): void => {
  ProfessionModule.SetSelectedFederalStates((ProfessionModule.selectedFederalStateItems || []).filter((el) => el.id !== id));
};

// The search term (and the AI search's words, which are the same term split up)
export const removeTextChip = (): void => {
  ProfessionModule.SetSearchTags({ tag: 'textTag', value: null });
  ProfessionModule.SetSearchTags({ tag: 'keywordTags', value: null });
  ProfessionModule.SetSearch({ obj: 'q', value: '' });
};

// One word of the AI search
export const removeKeywordChip = (word: string): void => {
  const keywordTags = (ProfessionModule.searchTags.keywordTags || []).filter((w: string) => w !== word);
  const q = keywordTags.join(' ');
  ProfessionModule.SetSearch({ obj: 'q', value: q });
  ProfessionModule.SetSearchTags({ tag: 'textTag', value: q || null });
  ProfessionModule.SetSearchTags({ tag: 'keywordTags', value: keywordTags.length ? keywordTags : null });
};

export const removeRadiusChip = (): void => {
  ProfessionModule.SetSearchTags({ tag: 'radius', value: null });
  ProfessionModule.SetSearch({ obj: 'radius', value: 0 });
};

// The field is gone for the catalog too (it reads ClustersModule)
export const removeClusterChip = (): void => {
  ProfessionModule.SetFilter({ obj: 'cluster_id', value: 0 });
  ProfessionModule.SetSearchTags({ tag: 'clusterTag', value: null });
  ClustersModule.SetClusterParams({ obj: 'cluster_id', value: 0 });
  ClustersModule.SetProfessionTitle('');
};

// A month of the course search ('first', '1'…'12', 'last')
export const removeMonthChip = (value: string): void => {
  const months = (ProfessionModule.searchTags.month || []).filter((item: string) => item !== value);
  ProfessionModule.SetSearch({ obj: 'month', value: months });
  ProfessionModule.SetSearchTags({ tag: 'month', value: months });
};

// ─── switch groups ───────────────────────────────────────────────────────────

// The type switches of the advanced search, as [filter, tag]. A filter at 1 excludes that type;
// all at 0 = no filter. The chips come from the filters, not from the tags: the advanced search,
// a shared link and the chips themselves each set the tags their own way.
export const SWITCH_GROUPS: Record<string, [string, string][]> = {
  // jobs on sites without the extended job filters
  employment: [['fulltime', 'fullTimeTag'], ['parttime', 'partTimeTag'], ['edujob', 'practiceTag']],
  // courses (the course search has two levels only)
  levelBasic: [['skill_level2', 'skillLevel2Tag'], ['skill_level3', 'skillLevel3Tag']],
  // jobs on sites with the extended job filters (groupExtendedJobFilters)
  kle: [['fulltime', 'fullTimeTag'], ['parttime', 'partTimeTag'], ['minijob', 'minijobTag'], ['edujob', 'practiceTag'], ['praktikum', 'praktikumTag']],
  origin: [['other_jobs', 'otherJobsTag'], ['amsjob', 'amsjobTag']],
  // jobs on every site
  level: [['skill_level1', 'skillLevel1Tag'], ['skill_level2', 'skillLevel2Tag'], ['skill_level3', 'skillLevel3Tag']],
  employer: [['dejob', 'dejobTag'], ['recruit', 'recruitTag'], ['tmpjob', 'tmpjobTag']],
};

const isExcluded = (filter: string): boolean => Number((ProfessionModule.jobFilter as any)[filter]) === 1;

// The filters of a group that get a chip: while the group filters (some type excluded), one chip
// per type still included — as the old list showed them. No type excluded = no chips.
export const includedSwitches = (group: string): string[] => {
  const filters = SWITCH_GROUPS[group].map(([filter]) => filter);
  if (!filters.some(isExcluded)) return [];
  return filters.filter((filter) => !isExcluded(filter));
};

// × on a chip excludes that type too. Once every type of the group is excluded, the group is
// reset to 0 — "no filter", all jobs again (as the old list did).
export const removeSwitchChip = (filter: string, group: string): void => {
  const switches = SWITCH_GROUPS[group];
  const tag = switches.find(([switchFilter]) => switchFilter === filter)?.[1];
  ProfessionModule.SetFilter({ obj: filter as any, value: 1 });
  if (tag) ProfessionModule.SetSearchTags({ tag: tag as any, value: false });
  if (switches.every(([switchFilter]) => isExcluded(switchFilter))) {
    switches.forEach(([switchFilter, switchTag]) => {
      ProfessionModule.SetFilter({ obj: switchFilter as any, value: 0 });
      ProfessionModule.SetSearchTags({ tag: switchTag as any, value: 0 });
    });
  }
};

// ─── job types without a switch ──────────────────────────────────────────────

// Job types the advanced search has no switch for. Only the AI search sets them (ticket 19788),
// and the list can only exclude a type (filter 1 = without these jobs).
export const AI_ONLY_TYPES = ['homeoffice', 'freelance', 'provision', 'thesis', 'seasonal'];

// Chip labels — no string ids yet, plain text until they are added to the translations
const AI_ONLY_TYPE_LABELS: Record<string, Record<string, string>> = {
  de: {
    homeoffice: 'Ohne Homeoffice',
    freelance: 'Ohne freiberufliche Stellen',
    provision: 'Ohne Provisionsbasis',
    thesis: 'Ohne Abschlussarbeiten',
    seasonal: 'Ohne Saisonarbeit',
  },
  en: {
    homeoffice: 'Without home office',
    freelance: 'Without freelance jobs',
    provision: 'Without commission-based jobs',
    thesis: 'Without thesis jobs',
    seasonal: 'Without seasonal jobs',
  },
};

export const aiOnlyTypeLabel = (filter: string, language: string): string => {
  const labels = AI_ONLY_TYPE_LABELS[language] || AI_ONLY_TYPE_LABELS.en;
  return labels[filter];
};

// The excluded ones get a chip
export const excludedAiOnlyTypes = (): string[] => AI_ONLY_TYPES.filter(isExcluded);

// × on the chip takes these jobs back in
export const removeAiOnlyTypeChip = (filter: string): void => {
  ProfessionModule.SetFilter({ obj: filter as any, value: 0 });
};
